import { useState } from "react";
import { useApp } from "../../lib/store";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { ChevronDown, ShieldAlert } from "lucide-react";

const ACTION_LABELS = {
  view: "act_view", create: "act_create", edit: "act_edit", delete: "act_delete",
  approve: "act_approve", export: "act_export", configure: "act_configure",
};

export function PermissionMatrix({ catalog, matrix, onChange, readOnly = false, affectedUsers = 0 }) {
  const { t } = useApp();
  const [open, setOpen] = useState({});
  const [confirm, setConfirm] = useState(null);
  const modules = catalog?.modules || [];
  const actions = catalog?.actions || [];
  const sensitive = new Set(catalog?.sensitive || []);

  const cell = (mod, act) => !!(matrix?.[mod]?.[act]);

  const apply = (mod, act, val) => {
    const next = { ...(matrix || {}) };
    next[mod] = { ...(next[mod] || {}), [act]: val };
    if (val && act !== "view") next[mod].view = true;
    if (!val && act === "view") actions.forEach((a) => { next[mod][a] = false; });
    onChange(next);
  };

  const toggle = (mod, act) => {
    if (readOnly) return;
    const val = !cell(mod, act);
    if (val && sensitive.has(`${mod}.${act}`)) {
      setConfirm({ mod, act });
      return;
    }
    apply(mod, act, val);
  };

  const setRow = (mod, val) => {
    if (readOnly) return;
    const next = { ...(matrix || {}) };
    next[mod] = {};
    actions.forEach((a) => { next[mod][a] = val; });
    onChange(next);
  };

  return (
    <div data-testid="permission-matrix">
      {affectedUsers > 0 && (
        <p className="text-xs text-[#E8871E] font-semibold mb-2" data-testid="matrix-affected-users">
          {affectedUsers} {t("iam_affected_users")}
        </p>
      )}

      {/* desktop */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50/70 border-b border-gray-100">
              <th className="text-left px-3 py-2 font-semibold text-[#1B2A4A]">{t("module_rights")}</th>
              {actions.map((a) => (
                <th key={a} className="px-2 py-2 font-medium text-gray-500 whitespace-nowrap">{t(ACTION_LABELS[a])}</th>
              ))}
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {modules.map((mod) => (
              <tr key={mod} className="border-b border-gray-50" data-testid={`matrix-row-${mod}`}>
                <td className="px-3 py-1.5 font-medium">{t(mod)}</td>
                {actions.map((a) => (
                  <td key={a} className="px-2 py-1.5 text-center">
                    <span className="inline-flex items-center gap-1">
                      <Checkbox checked={cell(mod, a)} disabled={readOnly}
                        onCheckedChange={() => toggle(mod, a)}
                        data-testid={`matrix-${mod}-${a}`} />
                      {sensitive.has(`${mod}.${a}`) && <ShieldAlert className="h-3 w-3 text-[#E8871E]" />}
                    </span>
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {!readOnly && (
                    <>
                      <button className="text-[10px] text-blue-600 hover:underline mr-2"
                        onClick={() => setRow(mod, true)} data-testid={`matrix-all-${mod}`}>all</button>
                      <button className="text-[10px] text-gray-400 hover:underline"
                        onClick={() => setRow(mod, false)} data-testid={`matrix-none-${mod}`}>none</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* mobile accordion */}
      <div className="md:hidden space-y-2">
        {modules.map((mod) => {
          const granted = actions.filter((a) => cell(mod, a));
          return (
            <div key={mod} className="rounded-lg border border-gray-200 bg-white" data-testid={`matrix-acc-${mod}`}>
              <button className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium"
                onClick={() => setOpen((o) => ({ ...o, [mod]: !o[mod] }))}
                data-testid={`matrix-acc-toggle-${mod}`}>
                <span>{t(mod)}</span>
                <span className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{granted.length}/{actions.length}</Badge>
                  <ChevronDown className={`h-4 w-4 transition-transform ${open[mod] ? "rotate-180" : ""}`} />
                </span>
              </button>
              {open[mod] && (
                <div className="px-3 pb-3 space-y-2">
                  {actions.map((a) => (
                    <label key={a} className="flex items-center justify-between text-sm py-1">
                      <span className="flex items-center gap-1.5">
                        {t(ACTION_LABELS[a])}
                        {sensitive.has(`${mod}.${a}`) && <ShieldAlert className="h-3 w-3 text-[#E8871E]" />}
                      </span>
                      <Checkbox checked={cell(mod, a)} disabled={readOnly}
                        onCheckedChange={() => toggle(mod, a)}
                        data-testid={`matrix-m-${mod}-${a}`} />
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-[#E8871E]" />{t("iam_sensitive_title")}
            </DialogTitle>
            <DialogDescription>{t("iam_sensitive_confirm")}</DialogDescription>
          </DialogHeader>
          {confirm && (
            <div className="space-y-3">
              <p className="text-sm font-mono bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                {t(confirm.mod)} → {t(ACTION_LABELS[confirm.act])}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setConfirm(null)}
                  data-testid="sensitive-cancel">{t("cancel")}</Button>
                <Button className="flex-1 bg-[#E8871E] hover:bg-[#d1751a] text-white"
                  data-testid="sensitive-confirm"
                  onClick={() => { apply(confirm.mod, confirm.act, true); setConfirm(null); }}>
                  {t("iam_grant")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
